// packages/core/src/progress/ProgressQueries.ts
// ─────────────────────────────────────────────────────────────────────────
// MODULE ROLLUPS + LIBRARY FACETS — pure derivations over the progress store
// and the learning content. This is the logic that used to live in
// useProgress.ts (module counts/status) plus the new Lesson Library facets.
//
// Kept separate from LessonProgress.ts so the model has no dependency on the
// learning-content module graph; this file is the join layer between the two.
// ─────────────────────────────────────────────────────────────────────────

import { getModule, LEARNING_MODULES, estimateReadMinutes } from '../learning';
import type { LearningModule, Lesson } from '../learning';
import { type ProgressStore } from './ProgressStore';
import {
	type LessonProgress,
	type CoarseStatus,
	type LessonProgressFlags,
	readLessonProgress,
	coarseStatus,
	isCompleted,
	progressFlags,
} from './LessonProgress';

// ── Module rollups (replaces useProgress module logic) ──────────────────────

export function getCompletedLessonIds(
	store: ProgressStore,
	moduleId: string,
): string[] {
	const mod = getModule(moduleId);
	if (!mod) return [];
	return mod.lessons
		.filter((l: Lesson) =>
			isCompleted(readLessonProgress(store, moduleId, l.id)),
		)
		.map((l: Lesson) => l.id);
}

export function getModuleCounts(
	store: ProgressStore,
	moduleId: string,
): { done: number; total: number } {
	const mod = getModule(moduleId);
	const total = mod?.lessons.length ?? 0;
	const done = getCompletedLessonIds(store, moduleId).length;
	return { done, total };
}

export function getModuleStatus(
	store: ProgressStore,
	moduleId: string,
): CoarseStatus {
	const { done, total } = getModuleCounts(store, moduleId);
	if (total === 0) return 'not_started';
	if (done >= total) return 'completed';
	if (done > 0) return 'in_progress';
	return 'not_started';
}

// ── Lesson Library facets ───────────────────────────────────────────────────
// A flat, filterable view over every lesson in LEARNING_MODULES, joined with
// its progress flags. `hasAudio` is supplied by the caller (it depends on the
// runtime audio manifest, which core resolves but the app fetches), defaulting
// to false so the library renders before the manifest loads.

export type LessonSurface = 'core' | 'school';

export interface LibraryLesson {
	lessonId: string;
	moduleId: string;
	moduleTitle: string;
	title: string;
	surface: LessonSurface;
	/** Derived reading time (minutes, fractional) from the lesson's prose. */
	readMinutes: number;
	/** Real audio length once durations are baked into the manifest. */
	listenSeconds?: number;
	hasAudio: boolean;
	flags: LessonProgressFlags;
	coarse: CoarseStatus;
}

export interface LibraryFacetInputs {
	/** lessonId → has at least one audio track (from the manifest). */
	hasAudio?: (lessonId: string) => boolean;
	/** lessonId → summed listen seconds for the chosen voice (Layer 0). */
	listenSeconds?: (lessonId: string) => number | undefined;
}

function moduleSurface(mod: LearningModule): LessonSurface {
	return mod.surface === 'school' ? 'school' : 'core';
}

export function buildLibrary(
	store: ProgressStore,
	inputs: LibraryFacetInputs = {},
): LibraryLesson[] {
	const out: LibraryLesson[] = [];
	for (const mod of LEARNING_MODULES) {
		const surface = moduleSurface(mod);
		for (const lesson of mod.lessons) {
			const record: LessonProgress = readLessonProgress(
				store,
				mod.id,
				lesson.id,
			);
			out.push({
				lessonId: lesson.id,
				moduleId: mod.id,
				moduleTitle: mod.title,
				title: lesson.title,
				surface,
				readMinutes: estimateReadMinutes(lesson.blocks),
				listenSeconds: inputs.listenSeconds?.(lesson.id),
				hasAudio: inputs.hasAudio?.(lesson.id) ?? false,
				flags: progressFlags(record),
				coarse: coarseStatus(record),
			});
		}
	}
	return out;
}

// ── Filter predicates (overlapping by design) ───────────────────────────────
// Library filters intentionally overlap: a lesson can match both Read and
// Completed. Each predicate is independent; the UI ORs within a facet group
// and ANDs across groups, but that composition lives in the view.

export type LibraryStatusFilter =
	| 'not_started'
	| 'in_progress'
	| 'read'
	| 'listened'
	| 'completed';

export function matchesStatus(
	l: LibraryLesson,
	filter: LibraryStatusFilter,
): boolean {
	switch (filter) {
		case 'not_started':
			return !l.flags.started;
		case 'in_progress':
			return l.flags.inProgress;
		case 'read':
			return l.flags.read;
		case 'listened':
			return l.flags.listened;
		case 'completed':
			return l.flags.completed;
	}
}

// ── Playlist listen-time total (Layer 0 / Decision 1) ────────────────────────
// Sums real audio durations across a playlist's resolvable lessons. Returns
// seconds; the view formats to "≈N min". Lessons without a known duration are
// skipped (so the total is a lower bound until every track has a baked
// duration — which, after the Option-A regeneration, is all of them).
